import { GridOptions } from 'ag-grid-community';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerExportFormat, ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { AgentStateSummaryCellRendererComponent } from './agent-state-summary-cell-renderer/agent-state-summary-cell-renderer.component';
import { AgentStateSummaryDialerCellRendererComponent } from './/agent-state-summary-dialer-cell-renderer/agent-state-summary-dialer-cell-renderer.component';
import { AgentStateSummaryData } from './agent-state-summary.model';
import { AgentStateSummaryService } from './agent-state-summary.service';
import { AgGridAngular } from 'ag-grid-angular';
import moment from "moment";

@Component({
    selector: 'cv-agent-state-summary',
    templateUrl: './agent-state-summary.component.html',
    styleUrls: ['./agent-state-summary.component.scss']
})
export class AgentStateSummaryComponent implements OnInit {
    @ViewChild('agentStateSummaryGrid') agGrid: AgGridAngular;
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    public data: AgentStateSummaryData[] = null; // A copy of data returned by loadData will be stored here.
    public actualFilter = '';
    public filterValue = ""; // This variable contains the contents of the search bar.
    public showFilter = false;

    // Ag-grid configuration:
    public gridOptions: GridOptions = null;
    public noData = false;
    dataLoadSuccess = false;

    public isCXOne = false;

    //For cxone search placeholder localization
    searchPlaceholder = "";
    localeText = {}; //Object holds localizations for ag grid

    private gridState: any = null; // This object holds previous state for ag-grid, and saves it to
    private isDataLoading = false;  // This is a bookkeeping variable used to prevent multiple outgoing requests for data.
    private isFirstLoad = true; // This variable is used for logging.  ModuleResponsiveController should accept isAutoRefresh and isFirstLoad variables.
    private stateSaveTimer: number;
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: AgentStateSummaryService,
        private config: CvConfigurationService,
    ) {
        this.isCXOne = config.isCxOne;
    }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; };

    async ngOnInit() {
        this.moduleManager.showLoader = true;

        const promises = [this.moduleManager.setMaxDateRange(90), this.loadLocalizations()];
        await Promise.all(promises);
        this.moduleManager.setMinDate(-90);//pass parameter in days.

        // Module Configuration:
        this.moduleManager.setOptions({
            name: 'Agent State Summary', // sets the title of the module (defaults to the database entry's ModuleName, if unset).
            enableWidthClass: false, // enabling this will add mediaquery-like classes to 'moduleResponsiveContainer'
            widgetMode: false, // enabling this disables the module header and moves the menu to the body of the module
            onSettingsApplied: settings => {
                this.loadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.FiveMinute,
            onRefresh: isAutoRefresh => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading, // determines whether a module's refresh menu item is clickable.  Can't refresh when there's already one in progress.
            onExport: this.onExport.bind(this),
        });

        this.setupGrid();

        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => this.gridState,
            onStateLoaded: stateData => {
                this.gridState = stateData;
                if (this.agGrid?.api && this.gridState)
                    this.agGridHelperService.loadGridState(this.agGrid?.api, stateData);
            },
            // if autoload is true, this state will start loading immediately
            //  and call onStateLoaded() when done
            // to load manually set to false and call this.moduleManager.loadState("sortedColumn");
            autoLoad: true
        });
    }

    // Called by AngularJS when the search bar's value has changed.
    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            this.actualFilter = this.filterValue;
            this.actualFilter = this.actualFilter.trim();
            this.agGrid?.api.setGridOption('quickFilterText', this.actualFilter);
            if(this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0){
                this.agGrid?.api?.showNoRowsOverlay();
            }
            else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
    public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /**
     * Get formatted data ready to export
     *
     * @param format
     */
    public async onExport(format: ModuleManagerExportFormat) {
        const response = await this.service.getDataForExport(this.moduleKey, format, this.isCXOne);
        return response;
    }

    /**
     * Loads the localizations for this module, then sets up the grid.
     */
    private async loadLocalizations() {
        try {
            const localizationPromises = [
                this.cvLocalizationService.GetSection("Agent_State_Summary"),
                this.cvLocalizationService.GetSection("Common"),
                this.cvLocalizationService.GetSection("Error_Module"),
            ];
            await Promise.all(localizationPromises);
            this.localeText = await this.agGridHelperService.getLocalizations();
        } finally {
            this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
        }
    }

    /**
     * Picks the correct localization key based on the configuration service
     *
     * @returns Text to show for header
     */
    private getOutstateCodeHeaderText(): string {
        if (this.config.isCxOne) {
            return this.cvLocalizationService.getCachedText("Agent_State_Summary", "Agent_State", "Agent State");
        }
        return this.cvLocalizationService.getCachedText("Agent_State_Summary", "Outstate_Code", "Outstate Code");
    }

    /**
     * Sets up the grid options.  Should be called after localizations are loaded, because the column headers are localized.
     */
    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            //  Please read getRowNodeId()'s comment.
            overlayNoRowsTemplate:  this.isCXOne ? `<div class="error-container" style="padding-top: 48px;">
                <svg class="mb-2">
                    <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                </svg>
                <p class="cxone-error-text">
                    ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                </p>
            </div>` : null,
            defaultColDef: {
                headerClass: "uppercase",
                sortable: true,
                icons: this.config.isCxOne ? {
                    sortAscending: `<div class="sortIcons">
                                        <svg class="icon-10 fill-cxoneBlue">
                                            <use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use>
                                        </svg>
                                        <svg class="icon-10 fill-sort">
                                            <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                                        </svg>
                                    </div>`,
                    sortDescending: `<div class="sortIcons">
                                        <svg class="icon-10 fill-sort">
                                            <use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use>
                                        </svg>
                                        <svg class="icon-10 fill-cxoneBlue">
                                            <use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use>
                                        </svg>
                                    </div>`,
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: `<svg class="icon-20 fill-iconMenu">
                                <use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use>
                            </svg>`,
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                } : null,
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            columnDefs: [
                {
                    field: "agentStateName", // This maps your key name from your data to your column in ag grid
                    colId: "Outstate Code", // Specifying a column ID is optional, but if you change the fields, saved state won't match later.
                    headerName: this.getOutstateCodeHeaderText(),
                    cellRenderer: 'AgentStateSummaryDialerCellRendererComponent',
                    filter: "agTextColumnFilter",
                    headerClass: "uppercase",
                    resizable: true,
                },
                {
                    field: "formattedDuration",
                    colId: "duration",
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Summary", "Duration", "Duration"),
                    cellRenderer: 'AgentStateSummaryCellRendererComponent',
                    cellRendererParams: {
                        getLargerDurationExists: () => this.data?.some(row => row.formattedDuration.length >= 10)
                    },
                    filter: "agTextColumnFilter",
                    resizable: true,
                },
                {
                    field: "percentage",
                    colId: "percentage",
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Summary", "Percentage", "Percentage"),
                    filter: "agTextColumnFilter",
                    filterValueGetter: params => (params.data.percentage * 100).toFixed(2) + '%',
                    valueFormatter: val => (val.value * 100).toFixed(2) + '%',
                    resizable: true,
                },
            ],
            onGridReady: event => {
                if (this.data) {
                    this.agGrid?.api.setGridOption('rowData', this.data);
                    this.getTotalValue();
                }

                this.stateRestoreInProgress = true; // Skip state save until the grid is ready

                this.restoreTableState(); //Needed to restore columns size saved on the gridState.
                if (!this.gridState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the gridState is null.
                    this.queueStateSave();
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Allow state save again after events have finished processing this frame
            },
            // This function defines which field from the row data is the "primary key".  It's used for delta updates between different setRowData() calls.
            // This "ID" property is the same as the above columnDef.  Usually it'll be a group id.  If there is no "primary key" for the data you're trying to display, set immutableData to false above.
            getRowId: ({data: rowData}) => rowData.agentStateName,
            onSortChanged: () => this.queueStateSave(),
            onColumnMoved: () => this.queueStateSave(),
            onColumnPinned: () => this.queueStateSave(),
            onColumnVisible: () => this.queueStateSave(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.queueStateSave();
                }
            },
            onFilterChanged: () => this.queueStateSave(),
            onGridSizeChanged: async event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.queueStateSave();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreTableState();
                }
            },
            components: {
                AgentStateSummaryCellRendererComponent,
                AgentStateSummaryDialerCellRendererComponent
            },
            localeText: this.localeText,
            suppressDragLeaveHidesColumns: true,
            suppressScrollOnNewData: true,
            suppressNoRowsOverlay: true,
            rowHeight: 36
        });

        this.restoreTableState()
    }

    private restoreTableState() {
        if (this.gridState && this.gridState.columns && this.agGrid?.api) {
            this.agGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
        }
    };

    /**
     * Shows and hides the search/filter bar
     */
    private toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        this.showFilter = !this.showFilter;
        if (this.showFilter){
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
        else {
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        }
    }

    /**
    * Change input focus to the search bar.
    */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
        }, 200);
    }

    private async loadData(isAutoRefresh) {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
            this.noData = this.data.length === 0;

            if (!this.noData) {
                this.data.forEach((row) => {
                    row.agentStateName = this.cvLocalizationService.getCachedText("Agent_State_Summary", row.agentStateName, row.agentStateName);
                });
            }
            
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data);
                
                this.agGrid?.api.redrawRows(); // needed to ensure all rows update their formattedDurations minWidths in the cases that they should
            }
            // else, the grid isn't ready, but the onGridReady() event handler will load up its initial values.
            this.getTotalValue();
            this.moduleManager.showLoader = false;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.dataLoadSuccess = !this.noData;
            this.isDataLoading = false;
            this.isFirstLoad = false;
            this.applyFilter();
        }
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
    private queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(async () => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.gridState = this.agGridHelperService.getGridState(this.agGrid?.api);
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500/*ms*/);
    }

    getTotalValue() {
        // get total duration
        let data = [];
        this.agGrid?.api.forEachNodeAfterFilter((node) => {
            let nodeData = { ...node.data };
            nodeData.Percentage = parseFloat((nodeData.Percentage * 100).toFixed(2));
            data.push(nodeData);
        });
        const totalSeconds = data.reduce((sum, item) => sum + this.timeToSeconds(item.formattedDuration), 0);
        const formattedDuration = this.secondsToTime(totalSeconds);
        // get total percentage
        const totalPercentage = this.data.map((item: any) => item.percentage).reduce((acc, curr) => acc + curr, 0);
        const pinnedBottomRowData = this.data.length > 0 ? [{
            agentStateName: this.cvLocalizationService.getCachedText("Dispositions_List", "Total", "Total"),
            formattedDuration: formattedDuration,
            percentage: totalPercentage
        }] : null;

        this.agGrid?.api.setGridOption('pinnedBottomRowData', pinnedBottomRowData);
    }

    private timeToSeconds(time: string): number {
        const [hours, minutes, seconds] = time.split(':').map(Number);
        return hours * 3600 + minutes * 60 + seconds;
    }

    private secondsToTime(seconds: number): string {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return [hours, minutes, secs].map(val => val < 10 ? `0${val}` : val).join(':');
    }
}
